import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface TabItem {
  id: string;
  label: string;
  icon: string;
  href?: string;
  order?: number;
  badge?: number;
  active?: boolean;
}

interface BottomNavProps {
  tabs: TabItem[];
}

export function BottomNav({ tabs }: BottomNavProps) {
  const pathname = usePathname();
  
  const renderIcon = (iconName: string) => {
    // Simple emoji/text icons for now - you can replace with proper icon library later
    const iconMap: Record<string, string> = {
      'calendar': '📅',
      'heart': '💝',
      'edit': '✏️',
      'megaphone': '📢',
      'chart': '📊',
    };
    
    return iconMap[iconName] || '•';
  };

  // Sort tabs by order if available
  const sortedTabs = [...tabs].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t">
      <div className="flex items-center justify-around h-16 px-2">
        {sortedTabs.map((tab) => {
          const href = tab.href || `/${tab.id}`;
          const isActive = pathname === href || tab.active;
          
          return (
            <Link
              key={tab.id}
              href={href}
              className={cn(
                "flex flex-col items-center justify-center flex-1 py-2 px-1 transition-colors",
                "hover:bg-muted/50 rounded-lg",
                isActive 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="relative">
                <span className="text-xl">
                  {renderIcon(tab.icon)}
                </span>
                {tab.badge && tab.badge > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center text-xs p-0 min-w-[20px]"
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </Badge>
                )}
              </div>
              <span className="text-xs mt-1 font-medium">
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}